import React from "react";
import { useTranslation } from "react-i18next";
import {
  EmailBuilderAlignmentControl,
  EmailBuilderBoundedRange,
} from "../../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";
import { EmailBuilderSettingsPaddingSection } from "../controls/EmailBuilderSettingsPaddingSection";
import { SettingSection } from "../controls/SettingSection";
import {
  baselineButtonRowGap,
  baselineButtonRowPadding,
} from "../../domain/baselines";
import { isPaddingInactive } from "../settingsSectionActivation";
import type { ButtonRowBlock } from "../../domain/types";
import type { BlockSettingsProps } from "./types";
import { blockHasColumnOverflowInDocument } from "../../domain/column-content-min";
import { selectDocument } from "../../domain/selectors";
import { useEditorStore } from "../../domain/redux/hooks";
import { EmailBuilderColumnOverflowHint } from "../EmailBuilderColumnOverflowHint";

export function ButtonRowBlockSettings({
  block,
  patch,
  rowId,
}: BlockSettingsProps & { block: ButtonRowBlock }) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const hasColumnOverflow =
    rowId !== undefined &&
    blockHasColumnOverflowInDocument(document, rowId, block.id);

  return (
    <>
      <SettingSection sectionId="layout" title={t("Layout")}>
        <EmailBuilderAlignmentControl
          value={block.align}
          onChange={align => patch({ align })}
        />
        <EmailBuilderBoundedRange
          label={t("Gap")}
          value={block.gap}
          min={0}
          max={32}
          defaultValue={baselineButtonRowGap()}
          onChange={gap => {
            if (gap === undefined) {
              return;
            }
            patch({ gap });
          }}
        />
        {hasColumnOverflow ? <EmailBuilderColumnOverflowHint /> : null}
      </SettingSection>

      <SettingSection
        sectionId="padding"
        title={t("Padding")}
        activation={{
          mode: "optional",
          isActive: !isPaddingInactive(block.padding),
          onAdd: () => patch({ padding: { ...baselineButtonRowPadding() } }),
        }}
      >
        <EmailBuilderSettingsPaddingSection
          fieldLabel={t("Button row padding (px)")}
          value={block.padding}
          defaultValue={baselineButtonRowPadding()}
          onChange={padding => {
            patch({ padding: { ...padding } });
          }}
        />
      </SettingSection>
    </>
  );
}
